import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { getBaskets, getClientsWithMargin } from "../../../api/orderInitiation";
import { formatAvailableMargin } from "../utils";

const LAST_SELECTED_CLIENT_CODES_KEY = "orderInitiate_lastSelectedClientCodes";

function loadLastSelectedClientCodes() {
  try {
    const raw = window.localStorage.getItem(LAST_SELECTED_CLIENT_CODES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((c) => String(c ?? "").trim()).filter(Boolean);
  } catch {
    return [];
  }
}

function saveLastSelectedClientCodes(codes) {
  try {
    window.localStorage.setItem(
      LAST_SELECTED_CLIENT_CODES_KEY,
      JSON.stringify([...new Set(codes)])
    );
  } catch {
    /* ignore */
  }
}

/** Sum multipliers for the same broker_client_code across selected baskets. */
function sumMultipliersByCodeFromSelectedBaskets(baskets, selectedBasketIds) {
  const sumByCode = {};
  (baskets || []).forEach((basket) => {
    if (!selectedBasketIds.has(basket.id)) return;
    const clients = Array.isArray(basket.clients) ? basket.clients : [];
    clients.forEach((c) => {
      const code = String(c?.broker_client_code ?? "").trim();
      if (!code) return;
      const m = Number(c?.multiplier);
      const add = Number.isFinite(m) ? m : 0;
      sumByCode[code] = (sumByCode[code] || 0) + add;
    });
    // Legacy shape fallback (settings list without clients[])
    if (clients.length === 0 && Array.isArray(basket.broker_client_codes)) {
      basket.broker_client_codes.forEach((codeRaw) => {
        const code = String(codeRaw ?? "").trim();
        if (!code) return;
        sumByCode[code] = (sumByCode[code] || 0) + 1;
      });
    }
  });
  return sumByCode;
}

/** Basket list + selection + filter clients from selected baskets. */
export function useBasketsAndClients() {
  const [selectedBasketIndices, setSelectedBasketIndices] = useState(() => new Set());
  const [baskets, setBaskets] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [basketsLoading, setBasketsLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [marginToDeployByClientId, setMarginToDeployByClientId] = useState({});
  const [multiplierByClientId, setMultiplierByClientId] = useState({});
  const [checkedByClientId, setCheckedByClientId] = useState({});
  const [lastSelectedCodes, setLastSelectedCodes] = useState(() => loadLastSelectedClientCodes());
  /** Client ids whose multiplier was set from basket selection (not free manual edit). */
  const basketAppliedMultiplierIdsRef = useRef(new Set());
  const marginToDeployByClientIdRef = useRef(marginToDeployByClientId);
  marginToDeployByClientIdRef.current = marginToDeployByClientId;
  const multiplierByClientIdRef = useRef(multiplierByClientId);
  multiplierByClientIdRef.current = multiplierByClientId;

  // Fetch baskets-with-multipliers once on mount (Order Initiate)
  useEffect(() => {
    setBasketsLoading(true);
    getBaskets()
      .then((data) => {
        const list = data?.response ?? data ?? [];
        setBaskets(Array.isArray(list) ? list : []);
      })
      .catch((err) => console.error("Failed to fetch baskets", err))
      .finally(() => setBasketsLoading(false));
  }, []);

  // Fetch all clients once on mount
  useEffect(() => {
    setClientsLoading(true);
    getClientsWithMargin()
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.response ?? [];
        setAllClients(list);
      })
      .catch((err) => console.error("Failed to fetch clients", err))
      .finally(() => setClientsLoading(false));
  }, []);

  const hasBasketSelected = selectedBasketIndices.size > 0;

  // When no basket selected: show all clients. When basket(s) selected: show only clients in those baskets.
  const filteredClients = useMemo(() => {
    if (!hasBasketSelected) return allClients;

    const selectedCodes = new Set();
    baskets.forEach((basket) => {
      if (!selectedBasketIndices.has(basket.id)) return;
      const clients = Array.isArray(basket.clients) ? basket.clients : [];
      clients.forEach((c) => {
        const code = String(c?.broker_client_code ?? "").trim();
        if (code) selectedCodes.add(code);
      });
      (basket.broker_client_codes || []).forEach((code) => {
        const c = String(code ?? "").trim();
        if (c) selectedCodes.add(c);
      });
    });

    return allClients.filter((client) =>
      selectedCodes.has(String(client.broker_client_code ?? "").trim())
    );
  }, [hasBasketSelected, baskets, selectedBasketIndices, allClients]);

  // Apply summed basket multipliers into the multiplier field when selection changes.
  useEffect(() => {
    if (!hasBasketSelected) {
      // Clear only multipliers that were applied from baskets (keep unrelated manual state if any).
      const appliedIds = basketAppliedMultiplierIdsRef.current;
      if (appliedIds.size === 0) return;
      setMultiplierByClientId((prev) => {
        const next = { ...prev };
        appliedIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
      basketAppliedMultiplierIdsRef.current = new Set();
      return;
    }

    const sumByCode = sumMultipliersByCodeFromSelectedBaskets(baskets, selectedBasketIndices);
    const nextApplied = new Set();
    const updates = {};

    allClients.forEach((client) => {
      const code = String(client.broker_client_code ?? "").trim();
      if (!(code in sumByCode)) return;
      const id = client.id;
      if (id == null) return;
      updates[id] = String(sumByCode[code]);
      nextApplied.add(id);
    });

    setMultiplierByClientId((prev) => {
      const next = { ...prev };
      // Remove previous basket-applied values that are no longer in selection
      basketAppliedMultiplierIdsRef.current.forEach((id) => {
        if (!nextApplied.has(id)) delete next[id];
      });
      Object.assign(next, updates);
      return next;
    });
    basketAppliedMultiplierIdsRef.current = nextApplied;
  }, [hasBasketSelected, baskets, selectedBasketIndices, allClients]);

  const toggleBasket = useCallback((id) => {
    setSelectedBasketIndices((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const updateClientRow = useCallback((rowId, field, value) => {
    if (field === "marginToDeploy") {
      let clamped = value;
      const num = parseFloat(value);
      if (value !== "" && !Number.isNaN(num)) {
        if (num > 100) clamped = "100";
        else if (num < 0) clamped = "0";
      }
      const prevMargin = marginToDeployByClientIdRef.current[rowId] ?? "";
      if (String(prevMargin) === String(clamped)) return;
      setMarginToDeployByClientId((prev) => ({ ...prev, [rowId]: clamped }));
      // User actually changed % — drop manual multiplier so lots follow the new %.
      // Tabbing through an unchanged (or empty) field must not wipe multipliers.
      if (String(clamped).trim() !== "") {
        setMultiplierByClientId((prev) => {
          if (!(rowId in prev)) return prev;
          const next = { ...prev };
          delete next[rowId];
          return next;
        });
        basketAppliedMultiplierIdsRef.current.delete(rowId);
      }
    } else if (field === "multiplier") {
      const prevMult = multiplierByClientIdRef.current[rowId] ?? "";
      if (String(prevMult) === String(value)) return;
      setMultiplierByClientId((prev) => ({ ...prev, [rowId]: value }));
      // Manual edit — no longer treat as basket-applied
      basketAppliedMultiplierIdsRef.current.delete(rowId);
      // Manual multiplier is independent of %-to-deploy; clear % so required margin
      // is validated against full available margin (see useClientRows marginStatus).
      setMarginToDeployByClientId((prev) => ({ ...prev, [rowId]: "" }));
    } else if (field === "checked") {
      setCheckedByClientId((prev) => ({ ...prev, [rowId]: value }));
    }
  }, []);

  const handleClientHeaderCheckboxChange = useCallback((checked) => {
    setCheckedByClientId((prev) => {
      const next = { ...prev };
      filteredClients.forEach((c) => (next[c.id] = checked));
      return next;
    });
  }, [filteredClients]);

  /** Set margin-to-deploy % for many clients at once (e.g. common “apply to all” field). */
  const applyMarginToDeployToAllClients = useCallback((value, clientIds) => {
    if (!Array.isArray(clientIds) || clientIds.length === 0) return;
    let clamped = value;
    const num = parseFloat(value);
    if (value !== "" && !Number.isNaN(num)) {
      if (num > 100) clamped = "100";
      else if (num < 0) clamped = "0";
    }
    setMarginToDeployByClientId((prev) => {
      const next = { ...prev };
      clientIds.forEach((id) => {
        next[id] = clamped;
      });
      return next;
    });
    setMultiplierByClientId((prev) => {
      const next = { ...prev };
      clientIds.forEach((id) => {
        if (id in next) delete next[id];
        basketAppliedMultiplierIdsRef.current.delete(id);
      });
      return next;
    });
  }, []);

  /** Set manual multiplier for many clients at once; clears margin % per row (same as editing one multiplier). */
  const applyMultiplierToAllClients = useCallback((value, clientIds) => {
    if (!Array.isArray(clientIds) || clientIds.length === 0) return;
    setMultiplierByClientId((prev) => {
      const next = { ...prev };
      clientIds.forEach((id) => {
        next[id] = value;
        basketAppliedMultiplierIdsRef.current.delete(id);
      });
      return next;
    });
    setMarginToDeployByClientId((prev) => {
      const next = { ...prev };
      clientIds.forEach((id) => {
        next[id] = "";
      });
      return next;
    });
  }, []);

  const persistLastSelectedClients = useCallback(() => {
    const codes = allClients
      .filter((c) => checkedByClientId[c.id])
      .map((c) => String(c.broker_client_code ?? "").trim())
      .filter(Boolean);
    saveLastSelectedClientCodes(codes);
    setLastSelectedCodes(codes);
  }, [allClients, checkedByClientId]);

  const applyLastSelectedClients = useCallback((select) => {
    const codes = new Set(lastSelectedCodes);
    if (codes.size === 0) return;
    setCheckedByClientId((prev) => {
      const next = { ...prev };
      allClients.forEach((c) => {
        const code = String(c.broker_client_code ?? "").trim();
        if (codes.has(code)) next[c.id] = select;
      });
      return next;
    });
  }, [allClients, lastSelectedCodes]);

  const lastSelectedMatchingClients = useMemo(() => {
    const codes = new Set(lastSelectedCodes);
    if (codes.size === 0) return [];
    return allClients.filter((c) => codes.has(String(c.broker_client_code ?? "").trim()));
  }, [allClients, lastSelectedCodes]);

  const lastSelectedChecked =
    lastSelectedMatchingClients.length > 0 &&
    lastSelectedMatchingClients.every((c) => checkedByClientId[c.id]);
  const lastSelectedDisabled = lastSelectedMatchingClients.length === 0;

  return {
    baskets,
    selectedBasketIndices,
    hasBasketSelected,
    toggleBasket,
    filteredClients,
    basketsLoading,
    clientsLoading,
    marginToDeployByClientId,
    multiplierByClientId,
    checkedByClientId,
    updateClientRow,
    handleClientHeaderCheckboxChange,
    applyMarginToDeployToAllClients,
    applyMultiplierToAllClients,
    persistLastSelectedClients,
    applyLastSelectedClients,
    lastSelectedChecked,
    lastSelectedDisabled,
  };
}

/** Client rows derived from filtered clients + margin from scrip info + local inputs. */
export function useClientRows(filteredClients, marginRequiredFromMeta, marginToDeployByClientId, multiplierByClientId, checkedByClientId) {
  return useMemo(() => {
    const list = filteredClients ?? [];
    return list.map((item) => {
      const id = item.id;
      // API may return available_margin as number or as { available_margin: number }
      const rawMargin =
        item.available_margin != null &&
        typeof item.available_margin === "object" &&
        "available_margin" in item.available_margin
          ? item.available_margin.available_margin
          : item.available_margin ?? item.AvailableMargin ?? 0;
      const availableMarginRaw =
        Number(String(rawMargin).replace(/,/g, "").trim()) || 0;
      const availableMargin = formatAvailableMargin(rawMargin);
      const marginToDeploy = marginToDeployByClientId[id] ?? "";
      const rawPct = parseFloat(marginToDeploy);
      const pct = Number.isFinite(rawPct) ? Math.min(100, Math.max(0, rawPct)) : 0;
      const maxDeploy = availableMarginRaw * (pct / 100);
      const multiplierInput = multiplierByClientId[id];
      const hasManualMultiplier =
        multiplierInput !== undefined && String(multiplierInput).trim() !== "";

      /** Max whole multipliers that fit in deployable margin (pct of available). */
      let suggestedFromMargin = 1;
      if (marginRequiredFromMeta > 0 && pct > 0) {
        suggestedFromMargin = Math.max(
          0,
          Math.floor(maxDeploy / marginRequiredFromMeta)
        );
      }

      const multiplierValue = hasManualMultiplier
        ? parseInt(String(multiplierInput).trim(), 10) || 0
        : suggestedFromMargin;

      const requiredMarginVal = marginRequiredFromMeta * Math.max(0, multiplierValue);
      const requiredMargin =
        marginRequiredFromMeta > 0
          ? requiredMarginVal.toLocaleString("en-IN", { maximumFractionDigits: 0 })
          : "0";

      let marginStatus = null;
      if (marginRequiredFromMeta > 0) {
        const fitsAvailableMargin = requiredMarginVal <= availableMarginRaw;
        if (!hasManualMultiplier && pct > 0) {
          // Margin to Deploy % is set: validate against that slice of available margin only.
          if (suggestedFromMargin === 0) {
            marginStatus = "red";
          } else {
            marginStatus = requiredMarginVal <= maxDeploy ? "green" : "red";
          }
        } else {
          // Manual multiplier only, or no % (default multiplier in UI): use total available margin.
          marginStatus = fitsAvailableMargin ? "green" : "red";
        }
      }
      return {
        id,
        checked: checkedByClientId[id] ?? false,
        clientCode: item.broker_client_code ?? "—",
        clientName: item.name ?? "",
        availableMargin,
        marginToDeploy,
        multiplier:
          multiplierInput !== undefined && multiplierInput !== "" ? String(multiplierInput) : "",
        suggestedMultiplier: hasManualMultiplier
          ? "1"
          : pct > 0
            ? String(suggestedFromMargin)
            : "1",
        requiredMargin,
        marginStatus,
      };
    });
  }, [
    filteredClients,
    marginRequiredFromMeta,
    marginToDeployByClientId,
    multiplierByClientId,
    checkedByClientId,
  ]);
}
